import { useEffect, useState } from "react";
import { styled } from "styled-components";

import { usePatchTopicVisibilityMutation } from "api/topicsApi.slice";
import { SectionTitleUnderlined, UISwitch } from "common/components";
import { CopyLinkField } from "common/components/CopyLinkField";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { setCustomMapFields } from "domains/createMapDetail/actions";
import { SidePanelContainer } from "domains/createMapDetail/components/form/SidePanelContainer";
import { getCurrentMap } from "domains/createMapDetail/selectors";
import { getMapAccessList } from "domains/createShareSettings/actions";
import { AccessList } from "domains/createShareSettings/components/AccessList";
import { CreateLinkSection } from "domains/createShareSettings/components/CreateLinkSection";
import { InvitationBox } from "domains/createShareSettings/components/InvitationBox";
import { ShareSettingsHeader } from "domains/createShareSettings/components/ShareSettingsHeader";

export const ShareSettings = () => {
  const dispatch = useDispatch();
  const [patchTopicVisbility] = usePatchTopicVisibilityMutation();
  const map = useSelector(getCurrentMap);
  const [isPublic, setIsPublic] = useState<boolean>(map?.is_public ?? false);
  const translate = useTranslate();

  useEffect(() => {
    if (map?.id !== undefined) dispatch(getMapAccessList(map.id));
  }, [dispatch, map?.id]);

  useEffect(() => {
    if (map?.is_public !== undefined) setIsPublic(map?.is_public);
  }, [map?.is_public]);

  const onClick = () => {
    const newIsPublic = !isPublic;
    setIsPublic(newIsPublic);
    dispatch(setCustomMapFields({ is_public: newIsPublic }));
    if (map?.id !== undefined)
      void patchTopicVisbility({ body: { visibility: newIsPublic ? "public" : "private" }, topic_id: map.id });
  };

  const url =
    map?.id === undefined
      ? translate("app.create.save_public_link")
      : `${import.meta.env.VITE_WEBSITE_URL}/topics/${map.id}`;

  return (
    <>
      <SectionTitleUnderlined>{translate("app.create.share_settings")}</SectionTitleUnderlined>
      <Container>
        {map?.id !== undefined && (
          <Setting>
            <AccessList publicAccess={isPublic} mapId={map.id} />
          </Setting>
        )}
        {(map?.id === undefined || map?.can_make_public) && (
          <Setting>
            <Row>
              <ShareSettingsHeader>{translate("app.create.share_settings.public_access")}</ShareSettingsHeader>
              <UISwitch isOn={isPublic} onClick={onClick} />
            </Row>
            {isPublic && <CopyLinkField url={url} isDisabled={map?.id === undefined} />}
          </Setting>
        )}
        <Setting>
          <Row>
            <CreateLinkSection topicId={map?.id} />
          </Row>
        </Setting>
        <Setting>
          <ShareSettingsHeader>{translate("app.create.share_settings.invite_collaborators")}</ShareSettingsHeader>
          <InvitationBox />
        </Setting>
      </Container>
    </>
  );
};

const Container = styled(SidePanelContainer)`
  border-radius: 4px;
  margin-bottom: 1.5rem;
`;

const Setting = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.outline};
  margin-bottom: 1rem;
  padding-bottom: 1rem;

  &:last-child {
    border: 0;
    margin-bottom: 0;
    padding-bottom: 0rem;
  }
`;

const Row = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
